import {
  UploadDriveImportFile,
  PreviewDriveImportFile,
  AnalyzeDriveImportFile,
  saveDriveImportMapping,
  getDriveImportMappings,
  getDriveImportMapping,
  deleteDriveImportMapping,
  startDriveImport,
  getDriveImportStatus,
} from "../../DriveScout/API/models/DriveImportWizard";

const state = {
  columnMapping: {},
  currentPage: 1,
  rowsPerPage: 5,
  currentStep: 1,
  fileUploaded: false,
  fileDirectory: "",
  fileName: "",
  csvHeaders: [],
  previewData: [],
  availableFields: [],
  importOptions: {
    test: false,
  },
  importing: false,
  importResults: null,
  file: null,
  isDragging: false,
  loading: false,
  error: null,
  requiredFields: [],
  // Session type mapping
  availableSessionTypes: [], // School's active session types from API
  uniqueSessionTypeValues: [], // Extracted from CSV: [{value: string, count: number}]
  sessionTypeMapping: {}, // { "CSV Value": session_type_id }
  showSessionTypeMappingStep: false, // Whether to show the mapping step
  analyzingFile: false, // Loading state for analyze endpoint
  // Async import progress tracking
  importId: null,
  importStatus: null, // pending, processing, completed, failed
  importProgress: {
    total: 0,
    processed: 0,
    percentage: 0,
  },
  importFailureReason: null,
};

const mutations = {
  SET_DRIVE_IMPORT_COLUMN_MAPPING(state, mapping) {
    state.columnMapping = mapping;
  },
  SET_DRIVE_IMPORT_PAGE(state, page) {
    state.currentPage = page;
  },
  SET_DRIVE_IMPORT_STEP(state, step) {
    state.currentStep = step;
  },
  SET_DRIVE_IMPORT_FILE_UPLOADED(state, uploaded) {
    state.fileUploaded = uploaded;
  },
  SET_DRIVE_IMPORT_FILE_INFO(state, { directory, name }) {
    state.fileDirectory = directory;
    state.fileName = name;
  },
  SET_DRIVE_IMPORT_PREVIEW_DATA(state, { headers, data, fields }) {
    state.csvHeaders = headers;
    state.previewData = data;
    state.availableFields = fields;
  },
  SET_DRIVE_IMPORT_OPTIONS(state, options) {
    state.importOptions = { ...options };
  },
  SET_DRIVE_IMPORT_IMPORTING(state, importing) {
    state.importing = importing;
  },
  SET_DRIVE_IMPORT_RESULTS(state, results) {
    state.importResults = results;
  },
  RESET_DRIVE_IMPORT(state) {
    Object.assign(state, {
      columnMapping: {},
      currentPage: 1,
      rowsPerPage: 5,
      currentStep: 1,
      fileUploaded: false,
      fileDirectory: "",
      fileName: "",
      csvHeaders: [],
      previewData: [],
      availableFields: [],
      importOptions: {
        test: false,
      },
      importing: false,
      importResults: null,
      file: null,
      isDragging: false,
      loading: false,
      error: null,
      requiredFields: [],
      // Reset session type mapping state
      availableSessionTypes: [],
      uniqueSessionTypeValues: [],
      sessionTypeMapping: {},
      showSessionTypeMappingStep: false,
      analyzingFile: false,
      // Reset async import state
      importId: null,
      importStatus: null,
      importProgress: {
        total: 0,
        processed: 0,
        percentage: 0,
      },
      importFailureReason: null,
    });
  },
  SET_DRIVE_IMPORT_FILE(state, file) {
    state.file = file;
  },
  SET_DRIVE_IMPORT_DRAGGING(state, isDragging) {
    state.isDragging = isDragging;
  },
  SET_DRIVE_IMPORT_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_DRIVE_IMPORT_ERROR(state, error) {
    state.error = error;
  },
  SET_DRIVE_IMPORT_REQUIRED_FIELDS(state, fields) {
    state.requiredFields = fields;
  },
  SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES(state, sessionTypes) {
    state.availableSessionTypes = sessionTypes;
  },
  SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES(state, values) {
    state.uniqueSessionTypeValues = values;
  },
  SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING(state, mapping) {
    state.sessionTypeMapping = mapping;
  },
  SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP(state, show) {
    state.showSessionTypeMappingStep = show;
  },
  SET_DRIVE_IMPORT_ANALYZING_FILE(state, analyzing) {
    state.analyzingFile = analyzing;
  },
  SET_DRIVE_IMPORT_ID(state, importId) {
    state.importId = importId;
  },
  SET_DRIVE_IMPORT_STATUS(state, status) {
    state.importStatus = status;
  },
  SET_DRIVE_IMPORT_PROGRESS(state, progress) {
    state.importProgress = { ...progress };
  },
  SET_DRIVE_IMPORT_FAILURE_REASON(state, reason) {
    state.importFailureReason = reason;
  },
};

const actions = {
  PREVIEW_DRIVE_IMPORT_FILE({ commit }, data) {
    return PreviewDriveImportFile(data.school_id, data.file_id).then(
      (response) => {
        return response;
      },
    );
  },
  UPLOAD_DRIVE_IMPORT_FILE({ commit }, data) {
    return UploadDriveImportFile(data.school_id, data.formData).then(
      (response) => {
        return response;
      },
    );
  },
  SAVE_DRIVE_IMPORT_MAPPING({ commit }, data) {
    return saveDriveImportMapping(data.school_id, data.name, data.mapping).then(
      (response) => {
        return response;
      },
    );
  },
  GET_DRIVE_IMPORT_MAPPINGS({ commit }, data) {
    return getDriveImportMappings(data.school_id).then((response) => {
      return response;
    });
  },
  GET_DRIVE_IMPORT_MAPPING({ commit }, data) {
    return getDriveImportMapping(data.school_id, data.mapping_id).then(
      (response) => {
        return response;
      },
    );
  },
  DELETE_DRIVE_IMPORT_MAPPING({ commit }, data) {
    return deleteDriveImportMapping(data.school_id, data.mapping_id).then(
      (response) => {
        return response;
      },
    );
  },
  ANALYZE_DRIVE_IMPORT_FILE({ commit }, data) {
    commit("SET_DRIVE_IMPORT_ANALYZING_FILE", true);
    return AnalyzeDriveImportFile(
      data.school_id,
      data.file_id,
      data.column_mapping,
    )
      .then((response) => {
        return response;
      })
      .finally(() => {
        commit("SET_DRIVE_IMPORT_ANALYZING_FILE", false);
      });
  },
  START_DRIVE_IMPORT({ commit }, data) {
    return startDriveImport(
      data.school_id,
      data.file_id,
      data.test,
      data.column_mapping,
      data.session_type_mapping,
    ).then((response) => {
      return response;
    });
  },
  GET_DRIVE_IMPORT_STATUS({ commit }, data) {
    return getDriveImportStatus(data.school_id, data.import_id).then(
      (response) => {
        const statusData = response.data;
        commit("SET_DRIVE_IMPORT_STATUS", statusData.status);
        commit("SET_DRIVE_IMPORT_PROGRESS", statusData.progress);
        if (statusData.failure_reason) {
          commit("SET_DRIVE_IMPORT_FAILURE_REASON", statusData.failure_reason);
        }
        if (statusData.results) {
          commit("SET_DRIVE_IMPORT_RESULTS", statusData.results);
        }
        return response;
      },
    );
  },
  SET_DRIVE_IMPORT_COLUMN_MAPPING({ commit }, mapping) {
    commit("SET_DRIVE_IMPORT_COLUMN_MAPPING", mapping);
  },
  SET_DRIVE_IMPORT_PAGE({ commit }, page) {
    commit("SET_DRIVE_IMPORT_PAGE", page);
  },
  SET_DRIVE_IMPORT_STEP({ commit }, step) {
    commit("SET_DRIVE_IMPORT_STEP", step);
  },
  SET_DRIVE_IMPORT_FILE_UPLOADED({ commit }, uploaded) {
    commit("SET_DRIVE_IMPORT_FILE_UPLOADED", uploaded);
  },
  SET_DRIVE_IMPORT_FILE_INFO({ commit }, info) {
    commit("SET_DRIVE_IMPORT_FILE_INFO", info);
  },
  SET_DRIVE_IMPORT_PREVIEW_DATA({ commit }, data) {
    commit("SET_DRIVE_IMPORT_PREVIEW_DATA", data);
  },
  SET_DRIVE_IMPORT_OPTIONS({ commit }, options) {
    commit("SET_DRIVE_IMPORT_OPTIONS", options);
  },
  SET_DRIVE_IMPORT_IMPORTING({ commit }, importing) {
    commit("SET_DRIVE_IMPORT_IMPORTING", importing);
  },
  SET_DRIVE_IMPORT_RESULTS({ commit }, results) {
    commit("SET_DRIVE_IMPORT_RESULTS", results);
  },
  RESET_DRIVE_IMPORT({ commit }) {
    commit("RESET_DRIVE_IMPORT");
  },
  SET_DRIVE_IMPORT_FILE({ commit }, file) {
    commit("SET_DRIVE_IMPORT_FILE", file);
  },
  SET_DRIVE_IMPORT_DRAGGING({ commit }, isDragging) {
    commit("SET_DRIVE_IMPORT_DRAGGING", isDragging);
  },
  SET_DRIVE_IMPORT_LOADING({ commit }, loading) {
    commit("SET_DRIVE_IMPORT_LOADING", loading);
  },
  SET_DRIVE_IMPORT_ERROR({ commit }, error) {
    commit("SET_DRIVE_IMPORT_ERROR", error);
  },
  SET_DRIVE_IMPORT_REQUIRED_FIELDS({ commit }, fields) {
    commit("SET_DRIVE_IMPORT_REQUIRED_FIELDS", fields);
  },
  SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES({ commit }, sessionTypes) {
    commit("SET_DRIVE_IMPORT_AVAILABLE_SESSION_TYPES", sessionTypes);
  },
  SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES({ commit }, values) {
    commit("SET_DRIVE_IMPORT_UNIQUE_SESSION_TYPE_VALUES", values);
  },
  SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING({ commit }, mapping) {
    commit("SET_DRIVE_IMPORT_SESSION_TYPE_MAPPING", mapping);
  },
  SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP({ commit }, show) {
    commit("SET_DRIVE_IMPORT_SHOW_SESSION_TYPE_MAPPING_STEP", show);
  },
  SET_DRIVE_IMPORT_ID({ commit }, importId) {
    commit("SET_DRIVE_IMPORT_ID", importId);
  },
  SET_DRIVE_IMPORT_STATUS({ commit }, status) {
    commit("SET_DRIVE_IMPORT_STATUS", status);
  },
  SET_DRIVE_IMPORT_PROGRESS({ commit }, progress) {
    commit("SET_DRIVE_IMPORT_PROGRESS", progress);
  },
  SET_DRIVE_IMPORT_FAILURE_REASON({ commit }, reason) {
    commit("SET_DRIVE_IMPORT_FAILURE_REASON", reason);
  },
};

const getters = {
  driveImportColumnMapping: (state) => state.columnMapping,
  driveImportCurrentPage: (state) => state.currentPage,
  driveImportRowsPerPage: (state) => state.rowsPerPage,
  driveImportCurrentStep: (state) => state.currentStep,
  driveImportFileUploaded: (state) => state.fileUploaded,
  driveImportFileInfo: (state) => ({
    directory: state.fileDirectory,
    name: state.fileName,
  }),
  driveImportPreviewData: (state) => ({
    headers: state.csvHeaders,
    data: state.previewData,
    fields: state.availableFields,
  }),
  driveImportOptions: (state) => state.importOptions,
  driveImportImporting: (state) => state.importing,
  driveImportResults: (state) => state.importResults,
  driveImportFile: (state) => state.file,
  driveImportDragging: (state) => state.isDragging,
  driveImportLoading: (state) => state.loading,
  driveImportError: (state) => state.error,
  driveImportRequiredFields: (state) => state.requiredFields,
  driveImportMappedPreviewData: (state) => {
    if (!state.previewData.length) {
      return [];
    }

    return state.previewData.map((row) => {
      const mappedRow = {};
      Object.keys(state.columnMapping).forEach((csvCol) => {
        const systemField = state.columnMapping[csvCol];
        if (systemField) {
          mappedRow[systemField] = row[csvCol];
        }
      });
      return mappedRow;
    });
  },
  validateDriveImportMapping: (state) => {
    const mappedFields = Object.values(state.columnMapping);
    return state.requiredFields.every((field) => mappedFields.includes(field));
  },
  driveImportAvailableSessionTypes: (state) => state.availableSessionTypes,
  driveImportUniqueSessionTypeValues: (state) => state.uniqueSessionTypeValues,
  driveImportSessionTypeMapping: (state) => state.sessionTypeMapping,
  driveImportShowSessionTypeMappingStep: (state) =>
    state.showSessionTypeMappingStep,
  driveImportAnalyzingFile: (state) => state.analyzingFile,
  // Validates that all unique session type values have been mapped
  validateDriveImportSessionTypeMapping: (state) => {
    if (!state.showSessionTypeMappingStep) {
      return true; // No mapping step needed
    }
    if (state.uniqueSessionTypeValues.length === 0) {
      return true; // No values to map
    }
    // Check that every unique value has a mapping (including "skip" which is null)
    return state.uniqueSessionTypeValues.every(
      (item) => state.sessionTypeMapping[item.value] !== undefined,
    );
  },
  // Async import getters
  driveImportId: (state) => state.importId,
  driveImportStatus: (state) => state.importStatus,
  driveImportProgress: (state) => state.importProgress,
  driveImportFailureReason: (state) => state.importFailureReason,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
